import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { diffLines } from "diff";
import Navbar from "../../components/Navbar/Navbar";
import "./PromptManager.css";

function getApiBaseUrl() {
  if (window.location.hostname === "localhost") {
    return "http://localhost:5001";
  }
  return `${window.location.protocol}//${window.location.host}/api`;
}

const MODULE_LABELS = {
  "openai.py": "Ticket Analysis",
  "rca_job_manager.py": "RCA Analysis",
  "metrics_job_manager.py": "Weekly Metrics",
  "gchat_unify_manager.py": "Chat Deduplication",
  "daily_dipstick_manager.py": "Daily Dip Stick",
  "bucket_manager.py": "Ticket Root Cause Buckets",
  "gchat_bucket_manager.py": "Chat Root Cause Buckets",
};

function groupByModule(prompts) {
  const groups = {};
  for (const p of prompts) {
    const label = MODULE_LABELS[p.module] || p.module;
    if (!groups[label]) groups[label] = [];
    groups[label].push(p);
  }
  return groups;
}

function VariablePill({ name }) {
  return <span className="pm-variable-pill">{`{${name}}`}</span>;
}

function VersionBadge({ version }) {
  return <span className="pm-version-badge">v{version}</span>;
}

function ProposalPill({ count = 1 }) {
  return (
    <span className="pm-proposal-pill" title="A pending proposal is awaiting your review">
      {count} pending proposal{count !== 1 ? "s" : ""}
    </span>
  );
}

function DiffBlock({ oldText, newText }) {
  const parts = diffLines(oldText || "", newText || "");
  return (
    <div className="pm-diff-block">
      {parts.map((part, idx) => {
        const cls = part.added
          ? "pm-diff-added"
          : part.removed
          ? "pm-diff-removed"
          : "pm-diff-unchanged";
        const sign = part.added ? "+" : part.removed ? "-" : " ";
        const lines = part.value.replace(/\n$/, "").split("\n");
        return (
          <div key={idx} className={`pm-diff-segment ${cls}`}>
            {lines.map((line, lineIdx) => (
              <div key={lineIdx} className="pm-diff-line">
                <span className="pm-diff-sign">{sign}</span>
                <span className="pm-diff-text">{line || " "}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

function PromptCard({ prompt, pendingProposals = 0, onEdit, onReview }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="pm-card">
      <div className="pm-card-header" onClick={() => setExpanded((p) => !p)}>
        <div className="pm-card-title-row">
          <span className="pm-card-name">{prompt.name}</span>
          <VersionBadge version={prompt.version} />
          {pendingProposals > 0 && <ProposalPill count={pendingProposals} />}
        </div>
        <p className="pm-card-description">{prompt.description}</p>
        {prompt.template_variables && prompt.template_variables.length > 0 && (
          <div className="pm-card-vars">
            <span className="pm-card-vars-label">Variables: </span>
            {prompt.template_variables.map((v) => (
              <VariablePill key={v} name={v} />
            ))}
          </div>
        )}
        <div className="pm-card-meta">
          {prompt.updated_at && (
            <span>Last updated: {new Date(prompt.updated_at).toLocaleString()}</span>
          )}
          {prompt.updated_by && prompt.updated_by !== "system" && (
            <span> by {prompt.updated_by}</span>
          )}
        </div>
        <button className="pm-expand-btn" onClick={(e) => { e.stopPropagation(); setExpanded((p) => !p); }}>
          {expanded ? "▲ Hide prompts" : "▼ View prompts"}
        </button>
      </div>

      {expanded && (
        <div className="pm-card-body">
          {prompt.system_prompt && (
            <div className="pm-prompt-section">
              <div className="pm-prompt-label">System Prompt</div>
              <pre className="pm-prompt-pre">{prompt.system_prompt}</pre>
            </div>
          )}
          {prompt.user_prompt_template && (
            <div className="pm-prompt-section">
              <div className="pm-prompt-label">User Prompt Template</div>
              <pre className="pm-prompt-pre">{prompt.user_prompt_template}</pre>
            </div>
          )}
          <div className="pm-card-actions">
            <button className="pm-btn pm-btn-primary" onClick={() => onEdit(prompt)}>
              Edit Prompt
            </button>
            {pendingProposals > 0 && (
              <button
                className="pm-btn pm-btn-proposal"
                onClick={() => onReview(prompt)}
              >
                Review proposal
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EditModal({ prompt, onClose, onSaved, initialTab = "edit", proposalsForKey = [] }) {
  const [systemPrompt, setSystemPrompt] = useState(prompt.system_prompt || "");
  const [userPrompt, setUserPrompt] = useState(prompt.user_prompt_template || "");
  const [changeNote, setChangeNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedVersion, setSelectedVersion] = useState(null);

  const [proposal, setProposal] = useState(null);
  const [loadingProposal, setLoadingProposal] = useState(false);
  const [proposalReviewNote, setProposalReviewNote] = useState("");
  const hasProposal = proposalsForKey.length > 0;

  const fetchVersions = useCallback(async () => {
    setLoadingVersions(true);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/prompts/${prompt.key}/versions`);
      setVersions(res.data);
    } catch {
      setVersions([]);
    } finally {
      setLoadingVersions(false);
    }
  }, [prompt.key]);

  const fetchProposal = useCallback(async () => {
    if (!hasProposal) return;
    setLoadingProposal(true);
    try {
      const proposalId = proposalsForKey[0].id;
      const res = await axios.get(`${getApiBaseUrl()}/prompts/proposals/${proposalId}`);
      setProposal(res.data);
    } catch (e) {
      setProposal(null);
    } finally {
      setLoadingProposal(false);
    }
  }, [hasProposal, proposalsForKey]);

  useEffect(() => {
    if (activeTab === "history") fetchVersions();
    if (activeTab === "proposal") fetchProposal();
  }, [activeTab, fetchVersions, fetchProposal]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const authToken = localStorage.getItem("authToken");
      const userStr = localStorage.getItem("user");
      const userEmail = userStr ? JSON.parse(userStr)?.email : "unknown";

      const res = await axios.put(
        `${getApiBaseUrl()}/prompts/${prompt.key}`,
        { system_prompt: systemPrompt, user_prompt_template: userPrompt, change_note: changeNote },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-User-Email": userEmail,
          },
        }
      );
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleAcceptProposal = async () => {
    if (!proposal) return;
    if (!window.confirm("Accept this proposal? It will become the new active prompt version.")) return;
    setSaving(true);
    setError(null);
    try {
      const authToken = localStorage.getItem("authToken");
      const userStr = localStorage.getItem("user");
      const userEmail = userStr ? JSON.parse(userStr)?.email : "unknown";

      const res = await axios.post(
        `${getApiBaseUrl()}/prompts/proposals/${proposal.id}/accept`,
        { review_note: proposalReviewNote },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-User-Email": userEmail,
          },
        }
      );
      onSaved(res.data.prompt);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to accept proposal");
    } finally {
      setSaving(false);
    }
  };

  const handleRejectProposal = async () => {
    if (!proposal) return;
    if (!window.confirm("Reject this proposal? It will be marked as rejected and not applied.")) return;
    setSaving(true);
    setError(null);
    try {
      const authToken = localStorage.getItem("authToken");
      const userStr = localStorage.getItem("user");
      const userEmail = userStr ? JSON.parse(userStr)?.email : "unknown";

      await axios.post(
        `${getApiBaseUrl()}/prompts/proposals/${proposal.id}/reject`,
        { review_note: proposalReviewNote },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-User-Email": userEmail,
          },
        }
      );
      onSaved(prompt);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to reject proposal");
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async (version) => {
    if (!window.confirm(`Revert to version ${version.version}? This will create a new version.`)) return;
    setSaving(true);
    setError(null);
    try {
      const authToken = localStorage.getItem("authToken");
      const userStr = localStorage.getItem("user");
      const userEmail = userStr ? JSON.parse(userStr)?.email : "unknown";

      const res = await axios.post(
        `${getApiBaseUrl()}/prompts/${prompt.key}/revert/${version.version}`,
        { change_note: `Reverted to version ${version.version}` },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-User-Email": userEmail,
          },
        }
      );
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to revert");
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewVersion = (v) => {
    setSelectedVersion(selectedVersion?.id === v.id ? null : v);
  };

  return (
    <div className="pm-modal-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pm-modal-header">
          <div>
            <h2 className="pm-modal-title">{prompt.name}</h2>
            <p className="pm-modal-desc">{prompt.description}</p>
            {prompt.template_variables && prompt.template_variables.length > 0 && (
              <div className="pm-modal-vars">
                <span className="pm-card-vars-label">Available variables: </span>
                {prompt.template_variables.map((v) => (
                  <VariablePill key={v} name={v} />
                ))}
              </div>
            )}
          </div>
          <button className="pm-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="pm-modal-tabs">
          <button
            className={`pm-tab ${activeTab === "edit" ? "pm-tab-active" : ""}`}
            onClick={() => setActiveTab("edit")}
          >
            Edit
          </button>
          <button
            className={`pm-tab ${activeTab === "history" ? "pm-tab-active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            Version History
          </button>
          {hasProposal && (
            <button
              className={`pm-tab pm-tab-proposal ${activeTab === "proposal" ? "pm-tab-active" : ""}`}
              onClick={() => setActiveTab("proposal")}
            >
              Pending Proposal
              <span className="pm-tab-badge">{proposalsForKey.length}</span>
            </button>
          )}
        </div>

        {activeTab === "edit" && (
          <div className="pm-modal-body">
            {prompt.system_prompt !== undefined && (
              <div className="pm-field">
                <label className="pm-label">
                  System Prompt
                  <span className="pm-label-hint">Defines the AI persona and behaviour constraints</span>
                </label>
                <textarea
                  className="pm-textarea pm-textarea-sm"
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  rows={5}
                />
              </div>
            )}
            {prompt.user_prompt_template !== undefined && (
              <div className="pm-field">
                <label className="pm-label">
                  User Prompt Template
                  <span className="pm-label-hint">
                    The message sent with each request. Use{" "}
                    {prompt.template_variables?.map((v) => (
                      <VariablePill key={v} name={v} />
                    ))}{" "}
                    as placeholders for dynamic data.
                  </span>
                </label>
                <textarea
                  className="pm-textarea pm-textarea-lg"
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  rows={16}
                />
              </div>
            )}
            <div className="pm-field">
              <label className="pm-label">
                Change Note
                <span className="pm-label-hint">Optional — describe what you changed and why</span>
              </label>
              <input
                className="pm-input"
                type="text"
                placeholder="e.g. Tightened CSAT scoring rubric"
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
              />
            </div>
            {error && <div className="pm-error">{error}</div>}
            <div className="pm-modal-footer">
              <span className="pm-current-version">Currently on v{prompt.version}</span>
              <div className="pm-footer-actions">
                <button className="pm-btn pm-btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button className="pm-btn pm-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving…" : `Save as v${prompt.version + 1}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="pm-modal-body">
            {loadingVersions && <div className="pm-loading">Loading versions…</div>}
            {!loadingVersions && versions.length === 0 && (
              <p className="pm-empty">No version history yet.</p>
            )}
            {!loadingVersions && versions.map((v) => (
              <div key={v.id} className="pm-version-row">
                <div className="pm-version-row-header">
                  <div className="pm-version-info">
                    <VersionBadge version={v.version} />
                    <span className="pm-version-date">
                      {v.changed_at ? new Date(v.changed_at).toLocaleString() : ""}
                    </span>
                    <span className="pm-version-author">{v.changed_by || ""}</span>
                  </div>
                  <div className="pm-version-actions">
                    <button
                      className="pm-btn pm-btn-ghost pm-btn-sm"
                      onClick={() => handlePreviewVersion(v)}
                    >
                      {selectedVersion?.id === v.id ? "Hide" : "Preview"}
                    </button>
                    <button
                      className="pm-btn pm-btn-secondary pm-btn-sm"
                      onClick={() => handleRevert(v)}
                      disabled={saving}
                    >
                      Restore
                    </button>
                  </div>
                </div>
                {v.change_note && (
                  <p className="pm-version-note">{v.change_note}</p>
                )}
                {selectedVersion?.id === v.id && (
                  <div className="pm-version-preview">
                    {v.system_prompt && (
                      <>
                        <div className="pm-prompt-label">System Prompt</div>
                        <pre className="pm-prompt-pre pm-prompt-pre-sm">{v.system_prompt}</pre>
                      </>
                    )}
                    {v.user_prompt_template && (
                      <>
                        <div className="pm-prompt-label">User Prompt Template</div>
                        <pre className="pm-prompt-pre pm-prompt-pre-sm">{v.user_prompt_template}</pre>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
            {error && <div className="pm-error">{error}</div>}
          </div>
        )}

        {activeTab === "proposal" && (
          <div className="pm-modal-body">
            {loadingProposal && <div className="pm-loading">Loading proposal…</div>}
            {!loadingProposal && !proposal && (
              <p className="pm-empty">No pending proposal for this prompt.</p>
            )}
            {!loadingProposal && proposal && (
              <>
                <div className="pm-proposal-meta">
                  <div className="pm-proposal-meta-row">
                    <span className="pm-proposal-meta-label">Based on</span>
                    <span className="pm-proposal-meta-value">v{proposal.based_on_version}</span>
                    <span className="pm-proposal-meta-label">Feedback used</span>
                    <span className="pm-proposal-meta-value">{proposal.feedback_count}</span>
                    {proposal.feedback_window && (
                      <>
                        <span className="pm-proposal-meta-label">Window</span>
                        <span className="pm-proposal-meta-value">
                          {proposal.feedback_window.year}-W{String(proposal.feedback_window.week_number).padStart(2, "0")}
                        </span>
                      </>
                    )}
                    <span className="pm-proposal-meta-label">Created</span>
                    <span className="pm-proposal-meta-value">
                      {proposal.created_at ? new Date(proposal.created_at).toLocaleString() : ""}
                    </span>
                  </div>
                </div>

                {proposal.change_summary && (
                  <div className="pm-proposal-summary">
                    <div className="pm-prompt-label">Change Summary</div>
                    <p className="pm-proposal-summary-text">{proposal.change_summary}</p>
                  </div>
                )}

                <div className="pm-prompt-section">
                  <div className="pm-prompt-label">System Prompt — Diff</div>
                  <DiffBlock
                    oldText={proposal.current_system_prompt || ""}
                    newText={proposal.proposed_system_prompt || ""}
                  />
                </div>

                <div className="pm-prompt-section">
                  <div className="pm-prompt-label">User Prompt Template — Diff</div>
                  <DiffBlock
                    oldText={proposal.current_user_prompt_template || ""}
                    newText={proposal.proposed_user_prompt_template || ""}
                  />
                </div>

                <div className="pm-field">
                  <label className="pm-label">
                    Review Note
                    <span className="pm-label-hint">Optional — recorded with the accept/reject decision</span>
                  </label>
                  <input
                    className="pm-input"
                    type="text"
                    placeholder="e.g. Looks good, accept"
                    value={proposalReviewNote}
                    onChange={(e) => setProposalReviewNote(e.target.value)}
                  />
                </div>

                {error && <div className="pm-error">{error}</div>}

                <div className="pm-modal-footer">
                  <span className="pm-current-version">
                    Status: <strong>{proposal.status}</strong>
                  </span>
                  <div className="pm-footer-actions">
                    <button
                      className="pm-btn pm-btn-secondary"
                      onClick={handleRejectProposal}
                      disabled={saving || proposal.status !== "pending"}
                    >
                      Reject
                    </button>
                    <button
                      className="pm-btn pm-btn-primary"
                      onClick={handleAcceptProposal}
                      disabled={saving || proposal.status !== "pending"}
                    >
                      {saving ? "Saving…" : `Accept & save as v${(proposal.based_on_version || prompt.version) + 1}`}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const PromptManager = () => {
  const [prompts, setPrompts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [editingTab, setEditingTab] = useState("edit");
  const [search, setSearch] = useState("");

  const fetchPrompts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${getApiBaseUrl()}/prompts`);
      setPrompts(res.data);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load prompts");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProposals = useCallback(async () => {
    try {
      const res = await axios.get(`${getApiBaseUrl()}/prompts/proposals`, {
        params: { status: "pending" },
      });
      setProposals(res.data || []);
    } catch {
      setProposals([]);
    }
  }, []);

  useEffect(() => {
    fetchPrompts();
    fetchProposals();
  }, [fetchPrompts, fetchProposals]);

  const proposalsByKey = proposals.reduce((acc, p) => {
    if (!acc[p.prompt_key]) acc[p.prompt_key] = [];
    acc[p.prompt_key].push(p);
    return acc;
  }, {});

  const handleSaved = (updated) => {
    if (updated && updated.key) {
      setPrompts((prev) =>
        prev.map((p) => (p.key === updated.key ? { ...p, ...updated } : p))
      );
    }
    setEditingPrompt(null);
    setEditingTab("edit");
    fetchProposals();
  };

  const handleEdit = (p) => {
    setEditingTab("edit");
    setEditingPrompt(p);
  };

  const handleReview = (p) => {
    setEditingTab("proposal");
    setEditingPrompt(p);
  };

  const filtered = prompts.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      p.key.toLowerCase().includes(q) ||
      (MODULE_LABELS[p.module] || p.module).toLowerCase().includes(q)
    );
  });

  const groups = groupByModule(filtered);

  return (
    <div className="pm-page">
      <Navbar />
      <div className="pm-container">
        <div className="pm-page-header">
          <div>
            <h1 className="pm-page-title">AI Prompt Manager</h1>
            <p className="pm-page-subtitle">
              Edit AI prompts and agent instructions without a code deployment. All changes are versioned.
            </p>
          </div>
          <div className="pm-header-stats">
            <div className="pm-stat">
              <span className="pm-stat-value">{prompts.length}</span>
              <span className="pm-stat-label">Prompts</span>
            </div>
            <div className="pm-stat">
              <span className="pm-stat-value">{Object.keys(groupByModule(prompts)).length}</span>
              <span className="pm-stat-label">Modules</span>
            </div>
          </div>
        </div>

        <div className="pm-search-row">
          <input
            className="pm-search"
            type="text"
            placeholder="Search prompts by name, description, or module…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading && <div className="pm-loading pm-loading-full">Loading prompts…</div>}
        {error && (
          <div className="pm-error-banner">
            <strong>Error:</strong> {error}
            <button className="pm-btn pm-btn-ghost pm-btn-sm" onClick={fetchPrompts} style={{ marginLeft: 12 }}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="pm-empty-state">
            <div className="pm-empty-icon">📝</div>
            <p>No prompts found{search ? ` matching "${search}"` : ""}.</p>
            {!search && (
              <p className="pm-empty-hint">
                Run <code>migrations/002_prompt_templates.sql</code> to seed the prompt library.
              </p>
            )}
          </div>
        )}

        {!loading && Object.entries(groups).map(([moduleName, modulePrompts]) => (
          <div key={moduleName} className="pm-module-section">
            <div className="pm-module-header">
              <h2 className="pm-module-name">{moduleName}</h2>
              <span className="pm-module-count">{modulePrompts.length} prompt{modulePrompts.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="pm-cards">
              {modulePrompts.map((p) => (
                <PromptCard
                  key={p.key}
                  prompt={p}
                  pendingProposals={(proposalsByKey[p.key] || []).length}
                  onEdit={handleEdit}
                  onReview={handleReview}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {editingPrompt && (
        <EditModal
          prompt={editingPrompt}
          initialTab={editingTab}
          proposalsForKey={proposalsByKey[editingPrompt.key] || []}
          onClose={() => {
            setEditingPrompt(null);
            setEditingTab("edit");
          }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default PromptManager;
